import * as React from "react";
import { useRef } from "react";
import PropTypes from "prop-types";
import { useTheme, styled } from "@mui/material/styles";
import Popper from "@mui/material/Popper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Autocomplete, { autocompleteClasses } from "@mui/material/Autocomplete";
import InputBase from "@mui/material/InputBase";
import {
  Box,
  Button,
  useMediaQuery,
  Typography,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";

import {
  ListboxComponent,
  useVirtualize,
} from "../../../../contexts/hooks/useVirtualize";
import AddIcon from "@mui/icons-material/Add";
import ImportButton from "../../ImportButton";
import { useState } from "react";

const StyledAutocompletePopper = styled("div")(({ theme }) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: "none",
    margin: 0,
    color: "inherit",
    fontSize: 13,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    padding: 0,
    backgroundColor: "#fff",
    ...theme.applyStyles("dark", {
      backgroundColor: "#1c2128",
    }),
    [`& .${autocompleteClasses.option}`]: {
      cursor: "default",
      minHeight: "auto",
      alignItems: "flex-start",
      padding: 8,
      borderBottom: "1px solid #eaecef",
      ...theme.applyStyles("dark", {
        borderBottom: "1px solid #30363d",
      }),
      '&[aria-selected="true"]': {
        backgroundColor: "transparent",
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
        {
          backgroundColor: theme.palette.action.hover,
        },
    },
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: "relative",
  },
}));

function PopperComponent(props) {
  const { disablePortal, anchorEl, open, ...other } = props;
  return <StyledAutocompletePopper {...other} />;
}

PopperComponent.propTypes = {
  anchorEl: PropTypes.any,
  disablePortal: PropTypes.bool,
  open: PropTypes.bool.isRequired,
};

const StyledPopper = styled(Popper)(({ theme }) => ({
  border: "1px solid #e1e4e8",
  boxShadow: `0 8px 24px ${"rgba(149, 157, 165, 0.2)"}`,
  color: "#24292e",
  backgroundColor: "#fff",
  borderRadius: 6,
  width: 300,
  zIndex: theme.zIndex.modal,
  fontSize: 13,

  ...theme.applyStyles("dark", {
    border: "1px solid #30363d",
    boxShadow: "0 8px 24px rgb(1, 4, 9)",
    color: "#c9d1d9",
    backgroundColor: "#1c2128",
  }),
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  padding: 10,
  width: "100%",
  borderBottom: "1px solid #eaecef",
  ...theme.applyStyles("dark", {
    borderBottom: "1px solid #30363d",
  }),
  "& input": {
    borderRadius: 4,
    padding: "8px 40px 8px 8px",
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    fontSize: 14,
    backgroundColor: "#fff",
    border: "1px solid #30363d",
    ...theme.applyStyles("dark", {
      backgroundColor: "#0d1117",
      border: "1px solid #eaecef",
    }),
    "&:focus": {
      //boxShadow: "0px 0px 0px 3px rgba(3, 102, 214, 0.3)",
      borderColor: "#0366d6",
      ...theme.applyStyles("dark", {
        boxShadow: "0px 0px 0px 3px rgb(12, 45, 107)",
        borderColor: "#388bfd",
      }),
    },
  },
}));

export default function AddIncludedFeatureAutoComplete({
  label = "",
  options = [],
  defaultValue = [],
  onSearch,
  searching,
  callBack,
  withApplyToAll,
  disabled = false,
}) {
  const { handleHighlightChange, internalListRef, handleItemsBuilt } =
    useVirtualize({ optionIndexMapRef: React.useRef(new Map()) });
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [pendingValue, setPendingValue] = React.useState([]);
  const debounceRef = useRef();
  const [inputValue, setInputValue] = React.useState("");

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const handleInputChange = (event, value, reason) => {
    if (reason === "input" || reason === "clear") {
      setInputValue(value);

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onSearch?.(value);
      }, 300);
    }
  };

  const mergeOptionsWithDefaults = (
    options = [],
    defaultValues = [],
    idField = "id",
  ) => {
    const map = new Map(options.map((o) => [o[idField], o]));

    for (const def of defaultValues) {
      if (!map.has(def[idField])) {
        map.set(def[idField], def);
      }
    }

    return Array.from(map.values());
  };

  const sortedOptions = React.useMemo(() => {
    const safePending = pendingValue ?? [];
    const safeDefaults = defaultValue ?? [];

    const pendingIds = new Set(safePending.map((p) => p.id));

    const mergedOptions = mergeOptionsWithDefaults(options, safeDefaults);

    // Remove items that are already selected/pending
    return mergedOptions.filter((opt) => !pendingIds.has(opt.id));

    /*return [...mergedOptions].sort((a, b) => {
      const aSelected = pendingIds.has(a.id);
      const bSelected = pendingIds.has(b.id);

      // Selected items go first
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // Then sort alphabetically (safe)
      return (a.displayName || "").localeCompare(b.displayName || "");
    });*/
  }, [options, pendingValue, defaultValue]);

  React.useEffect(() => {
    setPendingValue(defaultValue);
  }, [defaultValue]);

  const open = Boolean(anchorEl);
  const id = open ? "github-label" : undefined;

  return (
    <React.Fragment>
      <Box sx={{ fontSize: 13 }}>
        <Button
          variant="contained"
          color="warning"
          size="small"
          onClick={handleClick}
          startIcon={<AddCircleOutlinedIcon />}
          disabled={disabled || withApplyToAll?.isRunning}
        >
          {label}
        </Button>
      </Box>
      <StyledPopper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            <Autocomplete
              open
              multiple
              onClose={(event, reason) => {
                if (reason === "escape") {
                  handleClose();
                }
              }}
              inputValue={inputValue}
              value={pendingValue}
              disableCloseOnSelect
              renderValue={() => null}
              onInputChange={handleInputChange}
              noOptionsText="No result"
              renderOption={(props, option, state) => {
                return [
                  {
                    ...props,
                    onClick: (e) => e.preventDefault(), // prevent selecting when clicking row
                  },
                  option.displayName,
                  state.index,
                  option.id,
                  options.find((f) => f.id === option.id),
                ];
              }}
              options={sortedOptions}
              filterOptions={(x) => x}
              getOptionLabel={(option) => option.displayName || ""}
              onHighlightChange={handleHighlightChange}
              renderInput={(params) => (
                <Stack alignItems="center">
                  {withApplyToAll?.featureId && withApplyToAll?.enabled && (
                    <ImportButton
                      text="Apply To All"
                      sx={{ width: "94%", mt: 1 }}
                      startImport={withApplyToAll?.startImport}
                      isRunning={withApplyToAll?.isRunning}
                      onRunningChange={withApplyToAll?.setIsImportRunning}
                    />
                  )}

                  <StyledInput
                    ref={params.InputProps.ref}
                    inputProps={params.inputProps}
                    autoFocus
                    placeholder="Search Code"
                  />
                </Stack>
              )}
              slots={{
                popper: PopperComponent,
              }}
              slotProps={{
                listbox: {
                  component: ListboxComponent,
                  internalListRef,
                  onItemsBuilt: handleItemsBuilt,
                  role: "listbox",
                  pendingValue,
                  setPendingValue,
                  callBack,
                  searching,
                },
              }}
            />
          </div>
        </ClickAwayListener>
      </StyledPopper>
    </React.Fragment>
  );
}
